import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

export default function ChangePassword() {
  const [params] = useSearchParams();
  const forced = params.get("forced") === "1";
  const navigate = useNavigate();
  const [stage, setStage] = useState(1);
  const [otpSession, setOtpSession] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [loading, setLoading] = useState(false);

  const requestOtp = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/request-password-change-otp");
      setOtpSession(data.session_token);
      if (data.dev_otp) setDevOtp(data.dev_otp);
      setStage(2);
      toast.success("OTP sent via WhatsApp");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        new_password: newPassword, otp_session: otpSession, otp_code: otpCode,
      });
      toast.success("Password changed");
      navigate("/");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <Card className="w-full max-w-md rounded-sm border-slate-200 p-8" data-testid="change-password-page">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Security</div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: "Cabinet Grotesk, IBM Plex Sans, sans-serif" }}>
          {forced ? "Set your password" : "Change password"}
        </h1>
        <p className="text-sm text-slate-500 mb-6">
          {forced ? "First login requires you to set a new password and verify via WhatsApp OTP." : "Password change is verified via WhatsApp OTP."}
        </p>
        {stage === 1 && (
          <Button onClick={requestOtp} disabled={loading} data-testid="request-otp-btn"
            className="w-full bg-slate-900 hover:bg-slate-800 rounded-none h-11">
            Send WhatsApp OTP
          </Button>
        )}
        {stage === 2 && (
          <form onSubmit={submit} className="space-y-4" data-testid="change-pw-form">
            {devOtp && <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 p-2">DEV OTP: <b className="font-mono">{devOtp}</b></div>}
            <div>
              <Label>OTP Code</Label>
              <Input required value={otpCode} onChange={(e) => setOtpCode(e.target.value)} data-testid="pw-otp-input" className="rounded-none mt-1 font-mono tracking-widest" />
            </div>
            <div>
              <Label>New Password</Label>
              <Input required type="password" minLength={6} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="new-password-input" className="rounded-none mt-1" />
            </div>
            <Button type="submit" disabled={loading} data-testid="submit-pw-btn" className="w-full bg-slate-900 hover:bg-slate-800 rounded-none h-11">
              Change Password
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
